<i18n src="./resources/locales.json"></i18n>

<template>
  <v-container>
    <h1 class="ml-3 primary--text">{{ $t('workersCommission') }}</h1>
    <h2 class="ml-3 primary--text text-body-1">
      {{ $t('commissionSubtitle') }}
    </h2>
    <p v-if="formattedPeriodLabel" class="ml-3 text--secondary">
      {{ $t('periodShown') }}: {{ formattedPeriodLabel }}
    </p>

    <v-row
      class="tool-bar mt-6 ml-10 mb-4 mr-10 align-center"
      no-gutters
      style="flex-wrap: wrap"
    >
      <v-select
        v-model="selectedWorkerId"
        :items="allWorkers"
        item-value="id"
        item-text="name"
        :label="$t('worker')"
        background-color="primaryVariant2"
        outlined
        dense
        hide-details
        flat
        class="workers-report-filter mr-4 mb-2"
      />
      <v-menu
        v-model="dateRangeMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formattedDateRange"
            :label="$t('dateRange')"
            :placeholder="$t('selectDateRange')"
            background-color="primaryVariant2"
            outlined
            dense
            hide-details
            flat
            readonly
            prepend-inner-icon="mdi-calendar"
            class="workers-report-date-range mr-4 mb-2"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="dateRange"
          range
          scrollable
          show-adjacent-months
          no-title
          :locale="$i18n.locale"
          color="primary"
        >
          <v-spacer />
          <v-btn text color="primary" @click="dateRangeMenu = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="dateRange.length < 2"
            @click="dateRangeMenu = false"
          >
            {{ $t('ok') }}
          </v-btn>
        </v-date-picker>
      </v-menu>
      <v-btn
        color="primary"
        depressed
        class="mb-2"
        :disabled="!canGenerateReport"
        :loading="getIsWorkersReportLoading"
        @click="loadReport"
      >
        {{ $t('generateReport') }}
      </v-btn>
      <v-btn
        outlined
        color="primary"
        class="mb-2 ml-0 ml-sm-2"
        :disabled="!canGenerateReport || getIsWorkersReportLoading"
        :loading="printPdfLoading"
        @click="printPdf"
      >
        <v-icon left small>mdi-printer</v-icon>
        {{ $t('print') }}
      </v-btn>
      <v-checkbox
        v-model="hidePrice"
        :label="$t('hidePrice')"
        color="primary"
        hide-details
        dense
        class="mt-0 mb-2 ml-0 ml-sm-4"
      />
    </v-row>

    <v-row
      v-if="workerCommissionReport && !hidePrice"
      class="ml-10 mb-4 mr-10 align-end"
      no-gutters
    >
      <v-col cols="12" sm="auto">
        <v-text-field
          :value="formattedTotalCommission"
          :label="$t('totalCommission')"
          background-color="primaryVariant2"
          outlined
          dense
          hide-details
          flat
          readonly
          class="workers-report-total"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col class="ml-13">
        <v-data-table
          :headers="headers"
          :items="tableRows"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: $t('rowsPerPage')
          }"
          :items-per-page="20"
          class="elevation-1 ma-8"
          @click:row="openOrderDetail"
        >
          <template v-slot:[`footer.page-text`]="items">
            {{ items.pageStart }} - {{ items.pageStop }} {{ $t('of') }}
            {{ items.itemsLength }}
          </template>
          <template v-slot:no-data>
            <p>{{ $t('NoOrdersInDateRange') }}</p>
          </template>
        </v-data-table>
        <LoadingMask :isLoading="getIsWorkersReportLoading" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LoadingMask from '@/components/reusable-components/loading-mask/LoadingMask.vue';
import moment from 'moment';

export default {
  name: 'WorkersReport',

  data() {
    return {
      selectedWorkerId: null,
      dateRange: [],
      dateRangeMenu: false,
      printPdfLoading: false,
      hidePrice: false
    };
  },

  components: {
    LoadingMask
  },

  computed: {
    ...mapGetters('reports', ['workerCommissionReport']),
    ...mapGetters('workers', ['allWorkers']),
    ...mapGetters('loading', ['getIsWorkersReportLoading']),
    ...mapGetters('orders', ['getOrderById']),

    headers() {
      const cols = [
        {
          text: this.$t('orderNumber'),
          value: 'orderNumber',
          align: 'center',
          sortable: true,
          class: 'primary--text'
        },
        {
          text: this.$t('orderStatus'),
          value: 'orderStatus',
          align: 'left',
          sortable: true,
          class: 'primary--text'
        },
        {
          text: this.$t('engine'),
          value: 'engine',
          align: 'left',
          sortable: false,
          class: 'primary--text'
        },
        {
          text: this.$t('engineSeries'),
          value: 'engineSeries',
          align: 'left',
          sortable: false,
          class: 'primary--text'
        },
        {
          text: this.$t('service'),
          value: 'serviceName',
          align: 'left',
          sortable: false,
          class: 'primary--text'
        },
        {
          text: this.$t('subService'),
          value: 'subServiceName',
          align: 'left',
          sortable: false,
          class: 'primary--text'
        },
        {
          text: this.$t('jobPrice'),
          value: 'price',
          align: 'right',
          sortable: false,
          class: 'primary--text'
        },
        {
          text: this.$t('assignedAt'),
          value: 'assignedAt',
          align: 'center',
          sortable: false,
          class: 'primary--text'
        },
        {
          text: this.$t('finishedAt'),
          value: 'finishedAt',
          align: 'center',
          sortable: false,
          class: 'primary--text'
        },
        {
          text: this.$t('note'),
          value: 'note',
          align: 'left',
          sortable: false,
          class: 'primary--text'
        }
      ];
      return this.hidePrice ? cols.filter(c => c.value !== 'price') : cols;
    },

    selectedPeriodRange() {
      if (this.dateRange.length < 2) {
        return { fromDate: null, toDate: null };
      }
      const sorted = [...this.dateRange].sort((a, b) =>
        moment(a).isBefore(b) ? -1 : 1
      );
      return { fromDate: sorted[0], toDate: sorted[1] };
    },

    canGenerateReport() {
      return Boolean(this.selectedWorkerId) && this.dateRange.length === 2;
    },

    formattedDateRange() {
      const { fromDate, toDate } = this.selectedPeriodRange;
      if (!fromDate || !toDate) return '';
      const a = moment(fromDate).format('DD/MM/YYYY');
      const b = moment(toDate).format('DD/MM/YYYY');
      return a === b ? a : `${a} — ${b}`;
    },

    formattedPeriodLabel() {
      const r = this.workerCommissionReport;
      const startRaw = r && (r.periodStart ?? r.PeriodStart);
      const endRaw = r && (r.periodEnd ?? r.PeriodEnd);
      if (startRaw != null && endRaw != null) {
        const a = moment(startRaw).format('DD/MM/YYYY');
        const b = moment(endRaw).format('DD/MM/YYYY');
        return a === b ? a : `${a} — ${b}`;
      }
      return this.formattedDateRange;
    },

    tableRows() {
      const r = this.workerCommissionReport;
      const assignments = r && (r.finishedAssignments ?? r.FinishedAssignments);
      if (!Array.isArray(assignments)) {
        return [];
      }
      return assignments.map(a => {
        const rawPrice = a.price ?? a.Price;
        const priceNumber = Number(rawPrice);
        return {
          ...a,
          orderId: a.orderId ?? a.OrderId ?? null,
          orderNumber: a.orderNumber ?? a.OrderNumber ?? '—',
          orderStatus: a.orderStatus ?? a.OrderStatus ?? '—',
          engine: this.formatMotorDescription(a),
          engineSeries: this.formatEngineSeries(a),
          serviceName: a.serviceName ?? a.ServiceName ?? '—',
          subServiceName: a.subServiceName ?? a.SubServiceName ?? '—',
          price: this.formatPrice(rawPrice),
          priceValue: Number.isNaN(priceNumber) ? 0 : priceNumber,
          assignedAt: this.formatDateTime(
            a.assignedDateTime ?? a.AssignedDateTime
          ),
          finishedAt: this.formatDateTime(
            a.finishedDateTime ?? a.FinishedDateTime
          ),
          note: a.note ?? a.Note ?? '—'
        };
      });
    },

    totalCommissionSum() {
      return this.tableRows.reduce(
        (sum, row) => sum + (row.priceValue || 0),
        0
      );
    },

    formattedTotalCommission() {
      return `${this.totalCommissionSum.toFixed(2)} Bs.`;
    }
  },

  watch: {
    dateRange() {
      this.clearReportCache();
    },
    selectedWorkerId() {
      this.clearReportCache();
    }
  },

  async created() {
    this.$root.$on('workers-report-print-loading', this.setPrintPdfLoading);
    this.dateRange = [
      moment()
        .startOf('isoWeek')
        .format('YYYY-MM-DD'),
      moment()
        .endOf('isoWeek')
        .format('YYYY-MM-DD')
    ];
    await this.retrieveAllWorkers();
    if (this.allWorkers.length) {
      this.selectedWorkerId = this.allWorkers[0].id;
      await this.loadReport();
    }
  },

  beforeDestroy() {
    this.$root.$off('workers-report-print-loading', this.setPrintPdfLoading);
  },

  methods: {
    ...mapActions('reports', ['retrieveWorkerCommissionReport']),
    ...mapActions('workers', ['retrieveAllWorkers']),
    ...mapActions('orders', ['retrieveOrderById']),

    formatDateTime(v) {
      if (v == null || v === '') return '—';
      const m = moment(v);
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '—';
    },

    formatPrice(value) {
      if (value == null || value === '') return '—';
      const number = Number(value);
      if (Number.isNaN(number)) return '—';
      return number.toFixed(2);
    },

    formatEngineSeries(assignment) {
      const series =
        assignment.engineSeries ??
        assignment.EngineSeries ??
        assignment.engine ??
        assignment.Engine;
      return series && String(series).trim() ? String(series).trim() : '—';
    },

    formatMotorDescription(assignment) {
      const brand = assignment.brand ?? assignment.Brand;
      const cylinders =
        assignment.numberOfCylinders ?? assignment.NumberOfCylinders;
      const parts = [brand, cylinders]
        .map(v => (v != null ? String(v).trim() : ''))
        .filter(Boolean);
      if (parts.length) return parts.join(', ');

      const motor = assignment.engine ?? assignment.Engine;
      const motorText = motor != null ? String(motor).trim() : '';

      return motorText || '—';
    },

    async loadReport() {
      if (!this.canGenerateReport) return;

      const { fromDate, toDate } = this.selectedPeriodRange;
      if (!fromDate || !toDate) return;
      try {
        await this.retrieveWorkerCommissionReport({
          workerId: this.selectedWorkerId,
          fromDate,
          toDate
        });
      } catch (error) {
        this.$root.$emit('open-status-message-snackbar', {
          message: this.$t('errorLoadingReport'),
          messageType: 'error'
        });
      }
    },

    clearReportCache() {
      this.$store.commit('reports/setWorkerCommissionReport', null);
    },

    setPrintPdfLoading(value) {
      this.printPdfLoading = Boolean(value);
    },

    printPdf() {
      if (!this.canGenerateReport || !this.workerCommissionReport) return;
      if (!this.tableRows.length) return;
      const worker = this.allWorkers.find(w => w.id === this.selectedWorkerId);
      const columnLabels = this.headers.map(h => h.text);
      const rows = this.tableRows.map(row =>
        this.headers.map(h => {
          const v = row[h.value];
          if (v == null || v === '') return '—';
          return String(v);
        })
      );
      const priceColumnIndex = this.headers.findIndex(h => h.value === 'price');
      this.$root.$emit('print-workers-report-pdf', {
        fileName: `ComisionTrabajador_${(worker && worker.name) || 'worker'}`,
        title: this.$t('workersCommission'),
        subtitle: this.$t('commissionSubtitle'),
        periodLine: `${this.$t('periodShown')}: ${this.formattedPeriodLabel}`,
        workerLine: `${this.$t('worker')}: ${(worker && worker.name) || '—'}`,
        columnLabels,
        rows,
        totalLabel: this.$t('totalCommission'),
        totalValue: this.hidePrice ? null : this.formattedTotalCommission,
        totalColumnIndex: priceColumnIndex,
        emptyMessage: this.$t('NoOrdersInDateRange')
      });
    },

    async openOrderDetail(row) {
      const id = row.orderId;
      if (!id) return;
      await this.retrieveOrderById(id);
      this.$root.$emit(
        'open-order-detail-modal-info',
        Object.assign({}, this.getOrderById),
        true
      );
    }
  }
};
</script>
<style scoped>
@import './ScopedStyles.css';
</style>
